<template>
  <div class="impersonation-validate">
    <div class="page-head">
      <div class="page-ico"><i class="ik ik-shield" /></div>
      <div>
        <h1>Personificar</h1>
        <p>Informe o código fornecido pelo cliente para iniciar a sessão</p>
      </div>
    </div>

    <div
      v-if="loading"
      class="text-center text-muted">Carregando...</div>

    <div
      v-else
      class="d-flex justify-content-center">
      <div class="card validate-card">
        <div class="card-body">
          <label class="field-label">Código de acesso do cliente</label>
          <div class="input-group">
            <input
              v-model="code"
              type="text"
              class="form-control code-input"
              placeholder="K7M3 QP9X ZB42"
              :class="{ 'is-success': lookup !== null }"
              @keyup.enter="validate">
            <div class="input-group-append">
              <button
                class="btn btn-primary"
                :disabled="validating || code.length === 0"
                @click="validate">Validar</button>
            </div>
          </div>

          <div
            v-if="errorAlert !== null"
            class="alert mt-3"
            :class="'alert-' + errorAlert.tone"
            role="alert">
            <div class="alert-title"><i class="ik ik-alert-triangle" /> {{ errorAlert.title }}</div>
            <div class="alert-desc">{{ errorAlert.desc }}</div>
          </div>

          <template v-if="lookup !== null">
            <div class="valid-hint"><i class="ik ik-check-circle" /> Código válido — solicitação encontrada</div>

            <div class="request-box">
              <div class="request-label">SOLICITAÇÃO</div>
              <div class="client-row">
                <div class="avatar"><i class="ik ik-user" /></div>
                <div>
                  <div class="client-name">{{ lookup.clientName }}</div>
                  <div class="client-mail">{{ lookup.clientEmail }}</div>
                </div>
              </div>
              <div class="meta-row">
                <span>Criado em</span><b>{{ formatDate(lookup.createdAt) }}</b>
              </div>
              <div class="meta-row">
                <span>Expira em</span><b>{{ formatDate(lookup.expiratedAt) }}</b>
              </div>
              <div class="meta-row">
                <span>Status</span><b class="status-available">Disponível</b>
              </div>
            </div>

            <button
              class="btn btn-success btn-block btn-enter"
              :disabled="entering"
              @click="enter">
              <i class="ik ik-shield" /> Entrar como cliente
            </button>
            <p class="audit-note">Toda a sessão personificada é registrada para auditoria.</p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import loopback from '../../services/loopback'
import { friendlyPlainText } from '../../utils/error-message'

export default {
  name: 'ImpersonationValidate',
  data: () => ({
    code: '',
    loading: true,
    validating: false,
    entering: false,
    lookup: null,
    errorCode: null,
    errorFriendly: null
  }),
  mounted () {
    this.checkRollout()
  },
  computed: {
    errorAlert () {
      if (this.errorCode === null) return null
      const map = {
        INVALID: { tone: 'danger', title: 'Código inválido', desc: 'Nenhuma solicitação corresponde a este código. Confira com o cliente.' },
        EXPIRED: { tone: 'warning', title: 'Código expirado', desc: 'A solicitação expirou. Peça ao cliente para gerar um novo código.' },
        ALREADY_USED: { tone: 'danger', title: 'Código já utilizado', desc: 'Esta solicitação já está vinculada a outro agente de suporte.' },
        // TEL-2059: o /enter passou a validar o token antes de entregá-lo. Ao
        // reprovar, o LoopBack já destruiu o token — não há o que reaproveitar
        // nesta solicitação, o cliente precisa gerar um código novo.
        TOKEN_INVALID: { tone: 'warning', title: 'Sessão indisponível', desc: 'O acesso desta solicitação não é mais válido. Peça ao cliente para gerar um novo código.' },
        FORBIDDEN: { tone: 'danger', title: 'Acesso negado', desc: 'Você não tem permissão para usar a personificação de conta.' }
      }
      if (this.errorCode in map) return map[this.errorCode]
      // TEL-2243: o fallback exibia `error.message`, em ingles. O texto tecnico
      // continua disponivel no devtools (`rawMessage`), mas nao vai para a tela —
      // nem aqui, que e tela interna: em 5xx a mensagem crua e "Internal Server
      // Error", que nao ajuda nem o suporte.
      return {
        tone: 'danger',
        title: 'Não foi possível validar',
        desc: this.errorFriendly
      }
    }
  },
  methods: {
    checkRollout () {
      loopback
        .get('/rollout?featureFlag=account-impersonation')
        .then(rollout => {
          if (rollout !== 'ALLOWED') {
            this.$router.replace({ name: 'dashboard' })
            return
          }
          this.loading = false
        })
        .catch(() => {
          this.$router.replace({ name: 'dashboard' })
        })
    },
    validate () {
      this.errorCode = null
      this.errorFriendly = null
      this.lookup = null
      this.validating = true
      loopback
        .post('/account-impersonations/lookup', { code: this.code })
        .then(result => {
          this.lookup = result
        })
        .catch(error => {
          this.setError(error)
        })
        .finally(() => {
          this.validating = false
        })
    },
    enter () {
      this.errorCode = null
      this.errorFriendly = null
      this.entering = true
      loopback
        .post('/account-impersonations/enter', { code: this.code })
        .then(result => {
          return this.$store.dispatch('auth/startImpersonation', {
            accessToken: result.accessToken,
            client: result.client,
            impersonationId: result.impersonationId,
            expiratedAt: result.expiratedAt
          })
        })
        .then(() => {
          this.$router.push({ name: 'dashboard' })
        })
        .catch(error => {
          this.lookup = null
          this.setError(error)
        })
        .finally(() => {
          this.entering = false
        })
    },
    setError (error) {
      this.errorCode = error.errorCode === undefined ? 'UNKNOWN' : error.errorCode
      this.errorFriendly = friendlyPlainText(error,
        'Não conseguimos processar sua solicitação no momento. Tente novamente em alguns minutos.')
    },
    formatDate (date) {
      return new Date(date).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
$green: #16a34a;

.page-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 22px;
}
.page-ico {
  width: 44px;
  height: 44px;
  border-radius: 11px;
  background: #233244;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
}
.page-head h1 {
  margin: 0;
  font-size: 23px;
  font-weight: 700;
}
.page-head p {
  margin: 2px 0 0;
  font-size: 13.5px;
  color: #8a93a2;
}

.validate-card {
  width: 580px;
  max-width: 100%;
  padding: 12px;
}

.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #475467;
  display: block;
  margin-bottom: 8px;
}

.code-input {
  font-family: "SF Mono", "JetBrains Mono", ui-monospace, monospace;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  &.is-success {
    border-color: #bfe6cd;
    background: #f4fbf6;
  }
}

.alert-title {
  font-weight: 700;
  font-size: 13.5px;
}
.alert-desc {
  font-size: 12.5px;
  margin-top: 2px;
}

.valid-hint {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 12px;
  color: $green;
  font-size: 13px;
  font-weight: 600;
}

.request-box {
  background: #f7f8fb;
  border: 1px solid #e6e8ed;
  border-radius: 12px;
  padding: 16px 18px;
  margin: 18px 0;
}
.request-label {
  font-size: 11.5px;
  letter-spacing: 0.5px;
  color: #8a93a2;
  font-weight: 700;
  margin-bottom: 12px;
}
.client-row {
  display: flex;
  align-items: center;
  gap: 11px;
}
.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8a93a2;
}
.client-name {
  font-weight: 600;
  font-size: 14px;
}
.client-mail {
  font-size: 12.5px;
  color: #8a93a2;
}
.meta-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-top: 1px solid #eef0f3;
  font-size: 13.5px;
  margin-top: 6px;
  span {
    color: #8a93a2;
  }
}
.status-available {
  color: $green;
}

.btn-enter {
  margin-top: 4px;
}
.audit-note {
  text-align: center;
  font-size: 12px;
  color: #8a93a2;
  margin: 12px 0 0;
}
</style>



// WEBPACK FOOTER //
// src/components/Support/ImpersonationValidate.vue